import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBasePath } from "@/hooks/useBasePath";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { ArrowLeft, AlertTriangle, FileX } from "lucide-react";
import { TaxFormTab } from "@/components/TaxFormTab";
import { SECTION_META, ComplianceStatusBadge, SectionPdfButton } from "@/pages/ComplianceListPage";


const NotUploaded = ({ label }) => (
  <Card data-testid="compliance-not-uploaded">
    <CardContent className="p-12 flex flex-col items-center text-center">
      <FileX className="w-10 h-10 text-muted-foreground/50 mb-3" />
      <p className="font-medium">Not uploaded</p>
      <p className="text-sm text-muted-foreground mt-1">No {label} data has been uploaded or parsed for this investor yet.</p>
    </CardContent>
  </Card>
);

const MissingBanner = ({ fields }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="compliance-missing-banner">
    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
    <p className="text-sm text-amber-300">Missing: {fields.join(", ")}</p>
  </div>
);

export default function ComplianceDetailPage({ section }) {
  const { investorId, fundId } = useParams();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const [detail, setDetail] = useState(null);
  const listPath = `${basePath}/compliance/${section}`;
  const meta = SECTION_META[section];

  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/compliance/${section}/${investorId}`);
      if (fundId && data.fund_id !== fundId) throw new Error("Investor is not in this fund");
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to load investor");
      navigate(listPath);
    }
  }, [section, investorId, fundId, navigate, listPath]);

  useEffect(() => { load(); }, [load]);

  const save = async (sec, values, newName) => {
    try {
      await API.put(`/investors/${investorId}`, { tax_forms: { [sec]: values }, ...(newName ? { name: newName } : {}) });
      toast.success(`${meta.title} details updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  if (!detail) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid={`compliance-${section}-detail-page`}>
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(listPath)} data-testid="compliance-back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="compliance-investor-name">{detail.name}</h1>
            <ComplianceStatusBadge status={detail.status} testId="compliance-detail-status" />
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            {detail.email} | {detail.fund_name} | ID {detail.investor_display_id}
            {detail.investor_type && <Badge variant="secondary" className="ml-2 text-[10px]">{detail.investor_type}</Badge>}
          </p>
        </div>
      </div>
      {detail.status !== "complete" ? <NotUploaded label={meta.title} /> : (
        <>
          {detail.missing_fields.length > 0 && <MissingBanner fields={detail.missing_fields} />}
          <TaxFormTab section={section} investorType={detail.investor_type}
            headerAction={<SectionPdfButton section={section} investorId={investorId} hasSource={detail.has_source} testId={`${section}-download-pdf-btn`} />}
            data={detail.data} canEdit={detail.can_edit} onSave={save} />
        </>
      )}
    </div>
  );
}
